import { useEffect, useState } from 'react';
import Table, { Column } from '../components/Table';
import Button from '../components/Button';
import Modal from '../components/Modal';
import Input from '../components/Input';
import ConfirmDialog from '../components/ConfirmDialog';
import { employeeAccessService, MODULE_NAMES } from '../services/adminApi';

interface EmployeeRow {
  id: string;
  code: string;
  full_name: string;
  is_active: boolean;
  resignation_date?: string;
  module_access: Record<string, boolean>;
}

export default function EmployeeAccess() {
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResignedOnly, setShowResignedOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [message, setMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<'password' | null>(null);
  const [saving, setSaving] = useState(false);
  const [rowAction, setRowAction] = useState<{ type: 'reset' | 'resend' | 'welcome'; employee: EmployeeRow } | null>(null);
  const [rowActionSaving, setRowActionSaving] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<string | null>(null); // `${employeeId}:${moduleName}`
  const [pendingColumnToggle, setPendingColumnToggle] = useState<string | null>(null); // moduleName

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResignedOnly]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await employeeAccessService.list(showResignedOnly, search);
      setEmployees(res.data.employees || []);
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = async (employee: EmployeeRow, moduleName: string) => {
    const key = `${employee.id}:${moduleName}`;
    if (pendingToggle === key) return; // ignore double-clicks while a save is in flight
    const newValue = !employee.module_access[moduleName];
    setPendingToggle(key);
    try {
      // Send only the module that changed — merging the whole (possibly stale,
      // if another checkbox for this employee was just toggled) module_access
      // snapshot into the payload could silently revert an in-flight sibling
      // save's change, both in local state and in the database.
      await employeeAccessService.save(employee.id, { [moduleName]: newValue });
      setEmployees((prev) =>
        prev.map((e) =>
          e.id === employee.id ? { ...e, module_access: { ...e.module_access, [moduleName]: newValue } } : e
        )
      );
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save access change');
    } finally {
      setPendingToggle(null);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  };

  const allSelected = employees.length > 0 && selected.length === employees.length;
  const toggleSelectAll = () => {
    setSelected(allSelected ? [] : employees.map((e) => e.id));
  };

  // ADM-20: header checkbox per module column — bulk-sets that module's
  // access for every currently listed employee to the opposite of its
  // current "all checked" state (mirrors toggleSelectAll's semantics).
  const toggleModuleAll = async (moduleName: string) => {
    if (employees.length === 0 || pendingColumnToggle) return;
    const allChecked = employees.every((e) => !!e.module_access[moduleName]);
    const newValue = !allChecked;
    setPendingColumnToggle(moduleName);
    try {
      await Promise.all(employees.map((e) => employeeAccessService.save(e.id, { [moduleName]: newValue })));
      setEmployees((prev) =>
        prev.map((e) => ({ ...e, module_access: { ...e.module_access, [moduleName]: newValue } }))
      );
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save access change for all employees');
      load(); // some may have partially saved — reload to reflect actual state
    } finally {
      setPendingColumnToggle(null);
    }
  };

  const handleBulkResetPasswordClick = () => {
    if (!newPassword || selected.length === 0) return;
    setConfirmAction('password');
  };

  const performBulkResetPassword = async () => {
    setSaving(true);
    try {
      await employeeAccessService.bulkResetPassword(selected, newPassword);
      setMessage(`Password reset for ${selected.length} user(s)`);
      setResetModalOpen(false);
      setNewPassword('');
      setConfirmAction(null);
    } finally {
      setSaving(false);
    }
  };

  const ROW_ACTION_CONFIG = {
    reset: {
      title: 'Reset Password',
      confirmLabel: 'Reset Password',
      message: (name: string) => `Reset the password for ${name}? A new password will be generated and emailed to them.`,
      call: (employeeId: string) => employeeAccessService.resetPassword(employeeId),
    },
    resend: {
      title: 'Resend Login Details',
      confirmLabel: 'Resend',
      message: (name: string) =>
        `Resend login details to ${name}? Since the current password can't be recovered, this generates a new one and emails it.`,
      call: (employeeId: string) => employeeAccessService.resendLogin(employeeId),
    },
    welcome: {
      title: 'Send Welcome Email',
      confirmLabel: 'Send',
      message: (name: string) => `Send the welcome email to ${name}?`,
      call: (employeeId: string) => employeeAccessService.sendWelcomeEmail(employeeId),
    },
  } as const;

  const performRowAction = async () => {
    if (!rowAction) return;
    setRowActionSaving(true);
    try {
      const res = await ROW_ACTION_CONFIG[rowAction.type].call(rowAction.employee.id);
      const data = res.data || {};
      setMessage(
        data.newPassword && !data.emailed
          ? `${data.message} — new password: ${data.newPassword}`
          : data.message || 'Done'
      );
      setRowAction(null);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Action failed');
    } finally {
      setRowActionSaving(false);
    }
  };

  const columns: Column<EmployeeRow>[] = [
    {
      key: 'select',
      header: (
        <input
          type="checkbox"
          checked={allSelected}
          onChange={toggleSelectAll}
          title="Select all"
        />
      ),
      render: (row) => (
        <input type="checkbox" checked={selected.includes(row.id)} onChange={() => toggleSelect(row.id)} />
      ),
    },
    { key: 'code', header: 'Code', sortable: true },
    { key: 'full_name', header: 'Name', sortable: true },
    ...MODULE_NAMES.filter((m) => m !== 'Administration').map((m) => ({
      key: m,
      header: (
        <span className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={employees.length > 0 && employees.every((e) => !!e.module_access[m])}
            disabled={pendingColumnToggle === m}
            onChange={() => toggleModuleAll(m)}
            title={`Select all — ${m}`}
          />
          {m}
        </span>
      ),
      render: (row: EmployeeRow) => (
        <input
          type="checkbox"
          checked={!!row.module_access[m]}
          disabled={pendingToggle === `${row.id}:${m}` || pendingColumnToggle === m}
          onChange={() => toggleModule(row, m)}
        />
      ),
    })),
    {
      key: 'rowActions',
      header: 'Actions',
      render: (row) => (
        <div className="flex gap-2 whitespace-nowrap">
          <button
            onClick={() => setRowAction({ type: 'reset', employee: row })}
            className="text-blue-600 hover:underline text-xs"
          >
            Reset Password
          </button>
          <button
            onClick={() => setRowAction({ type: 'resend', employee: row })}
            className="text-blue-600 hover:underline text-xs"
          >
            Resend Login
          </button>
          <button
            onClick={() => setRowAction({ type: 'welcome', employee: row })}
            className="text-blue-600 hover:underline text-xs"
          >
            Welcome Email
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Employee Access</h1>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => setResetModalOpen(true)} disabled={selected.length === 0}>
            Reset Password (Bulk)
          </Button>
        </div>
      </div>

      {message && <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded px-4 py-2">{message}</div>}

      <div className="flex items-center gap-4 mb-4">
        <Input
          placeholder="Search employee..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
        />
        <Button variant="secondary" onClick={load}>
          Search
        </Button>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={showResignedOnly}
            onChange={(e) => setShowResignedOnly(e.target.checked)}
          />
          Show Resigned Employee only
        </label>
      </div>

      <Table columns={columns} rows={employees} loading={loading} emptyMessage="No employees found" />

      <Modal isOpen={resetModalOpen} onClose={() => setResetModalOpen(false)} title="Bulk Reset Password">
        <div className="space-y-4">
          <Input
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <Button className="w-full" onClick={handleBulkResetPasswordClick}>
            Reset Password for {selected.length} User(s)
          </Button>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={confirmAction === 'password'}
        title="Reset Password"
        message={`Are you sure you want to reset the password for ${selected.length} user(s)?`}
        confirmLabel="Reset Password"
        variant="primary"
        loading={saving}
        onConfirm={performBulkResetPassword}
        onCancel={() => setConfirmAction(null)}
      />

      <ConfirmDialog
        isOpen={!!rowAction}
        title={rowAction ? ROW_ACTION_CONFIG[rowAction.type].title : ''}
        message={rowAction ? ROW_ACTION_CONFIG[rowAction.type].message(rowAction.employee.full_name) : ''}
        confirmLabel={rowAction ? ROW_ACTION_CONFIG[rowAction.type].confirmLabel : 'Confirm'}
        variant="primary"
        loading={rowActionSaving}
        onConfirm={performRowAction}
        onCancel={() => setRowAction(null)}
      />
    </div>
  );
}
